<script>
  import { mapActions, mapState } from 'pinia';
  import { userStore } from '@/store/user';
  import { eventStore } from '@/store/event';
  import { platformStore } from '@/store/platform';
  // @deprecated - Old event list component, replaced by project-based navigation (ProjectsList + ProjectEventsView)
  // Keep for emergency rollback only - can be removed after successful testing
  import Events from '@/components/tickets/Events.vue';
  import ProjectsList from '@/components/tickets/ProjectsList.vue';
  import ProjectEventsView from '@/components/tickets/ProjectEventsView.vue';
  import SeatSelectionModal from '@/apps/tickets/components/SeatSelectionModal.vue';
  import TicketSelectionModal from '@/apps/tickets/components/TicketSelectionModal.vue';
  import dayjs from 'dayjs';
  import { cartStore } from '@/store/cart';
  import { cartV2Store } from '@/store/cartV2';
  import ShoppingCart from '@/components/cart/ShoppingCart.vue';
  import CheckoutModal from '@/components/cart/CheckoutModal.vue';
  import Spinner from '@/components/Spinner.vue';
  import PlatformService from '@/services/PlatformService';
  import ProjectService from '@/services/ProjectService';
  import TicketGroupService from '@/services/TicketGroupService';
  import { debugLog } from '@/utils/debug';
  import { setupAblyConnection } from '@/utils/ablyConnection';
  import { buildAblyPlatformChannelName } from '@/utils/ablyChannels';
  import { ablyTokens } from '@/config/ably';
  import {
    usesDeferredSeatAssignment,
    usesFreeSeatSelection,
    usesTicketSelectionFlow,
  } from '@/utils/ticketEventFlow';

  /**
   * Een verkoopblok is een ticketgroep of een project; het type bepaalt welk
   * endpoint de details en beschikbaarheid levert.
   */
  function isTicketGroup(block) {
    return block?.type === 'group';
  }

  const CART_CACHE_MAX_AGE_MS = 30000;
  const PROJECT_AVAILABILITY_REFRESH_INTERVAL_MS = 30000;
  const PROJECT_AVAILABILITY_STALE_AFTER_MS = 15000;

  export default {
    name: 'TicketsApp',
    components: {
      Events,
      ProjectsList,
      ProjectEventsView,
      SeatSelectionModal,
      TicketSelectionModal,
      ShoppingCart,
      CheckoutModal,
      Spinner,
    },
    props: {
      platform: {
        type: Object,
        default: () => {},
      },
    },
    data() {
      return {
        ably: setupAblyConnection(ablyTokens.tickets),
        channel: null, // Single realtime channel for all ticket/event updates
        connected: false,
        selectedEvent: null,
        numTickets: 0,
        amount: 0,
        showCheckout: false,
        showSeatSelectionModal: false,
        showTicketSelectionModal: false,
        publishDebounceTimer: null,
        publishDebounceDelay: 300, // 300ms debounce delay
        refreshDebounceTimer: null, // Timer for debouncing project refresh on Ably updates
        projectAvailabilityRefreshTimer: null,
        cartReservationRefreshTimer: null,
        projectAvailabilityRefreshInFlight: false,
        projectAvailabilityLastLoadedAt: 0,
        // Project navigation
        currentView: 'projects', // 'projects' | 'project-events' | 'events'
        // De verkoopblokken in het overzicht: ticketgroepen en projecten door elkaar,
        // elk met een 'type' ('group' of 'project') dat bepaalt welk endpoint erbij hoort.
        projects: [],
        selectedProject: null,
        loadingProjects: false,
      };
    },
    computed: {
      ...mapState(userStore, ['currentUser']),
      ...mapState(platformStore, ['currentPlatform']),
      ...mapState(cartStore, ['cart']),
      ...mapState(cartV2Store, { cartV2: (state) => state }),
      ...mapState(eventStore, ['events', 'loadingEvents']),
      sessionUserId() {
        return this.currentUser ? this.currentUser.id : Date.now();
      },
      useProjectNavigation() {
        // Always use project-based navigation (new default)
        // To rollback to old event list: change this to return false
        return true;
      },
      filteredEvents() {
        // Return events from selected project (new API returns events within project data)
        if (this.currentView === 'project-events' && this.selectedProject) {
          return this.selectedProject.events || [];
        }
        return this.events;
      },
      existingCartTicketsForSelectedEvent() {
        // Get cart tickets for the currently selected event from V2 cart
        if (!this.selectedEvent || !this.cartV2.items) {
          debugLog('TicketsApp: No selected event or cart items');
          return [];
        }

        debugLog(
          'TicketsApp: Computing existingCartTickets for event:',
          this.selectedEvent.id
        );
        debugLog('TicketsApp: Cart V2 items:', this.cartV2.items);

        // Filter cart items for this event and map to expected format
        const tickets = this.cartV2.items
          .filter((item) => {
            // V2 API uses 'type' field, not 'item_type'
            const isTicket = item.type === 'ticket';
            const eventIdMatches =
              parseInt(item.metadata?.event_id) ===
              parseInt(this.selectedEvent.id);
            const matches = isTicket && eventIdMatches;
            debugLog(
              'TicketsApp: Checking item:',
              item,
              'isTicket:',
              isTicket,
              'eventIdMatches:',
              eventIdMatches,
              'matches:',
              matches
            );
            return matches;
          })
          .flatMap((item) => {
            // Create an array entry for each quantity
            // This matches the old cart format where each ticket was a separate entry
            const tickets = [];
            for (let i = 0; i < item.quantity; i++) {
              tickets.push({
                type: item.item_id, // item_id is the ticket type ID
                ticket: {
                  type: {
                    id: item.item_id,
                  },
                },
                metadata: item.metadata,
              });
            }
            return tickets;
          });

        debugLog('TicketsApp: Returning existingCartTickets:', tickets);
        return tickets;
      },
      hasCartItems() {
        // Check if cart has any items
        return (this.cartV2.items?.length || 0) > 0;
      },
    },
    async mounted() {
      this.setPlatform(this.platform);
      this.setInitialFeatures();

      // Load projects for project-based navigation (new default)
      await this.loadProjects();

      // After projects are loaded, check if we need to load a specific project from URL
      await this.loadProjectFromUrl();

      window.addEventListener('focus', this.handleProjectAvailabilityFocus);
      document.addEventListener(
        'visibilitychange',
        this.handleProjectAvailabilityVisibilityChange
      );

      // Setup Ably connection handler
      const setupAblyChannel = async () => {
        this.connected = true;

        // NEW: Single realtime channel for all events
        // Normalize channel name: lowercase with underscores instead of spaces
        const realtimeChannelName = buildAblyPlatformChannelName(
          this.currentPlatform.name,
          'realtime'
        );
        this.channel = this.ably.channels.get(realtimeChannelName);
        debugLog('Realtime channel connected:', realtimeChannelName);

        // Subscribe to backend ticket events (backend is authoritative)
        this.channel.subscribe('ticket.reserved', (message) => {
          debugLog('Ticket reserved (backend):', message.data);
          this.updateEventFromBackend(message.data);
        });

        this.channel.subscribe('ticket.released', (message) => {
          debugLog('Ticket released (backend):', message.data);
          this.updateEventFromBackend(message.data);
        });

        this.channel.subscribe('ticket.sold', (message) => {
          debugLog('Ticket sold (backend):', message.data);
          this.updateEventFromBackend(message.data);
          // Don't reload cart here - cart is cleared after checkout
        });

        // Subscribe to maintenance mode events
        this.channel.subscribe('platform.maintenance', async (message) => {
          const { platformDomain } = message.data;

          // Check if this message is for the current platform
          if (window.viploge?.platform?.domain === platformDomain) {
            // Always check the actual current status from the backend
            // This prevents issues with stale Ably messages
            try {
              const response = await PlatformService.getMaintenanceStatus();
              if (response.data.maintenanceMode) {
                // Only redirect if maintenance mode is actually enabled
                window.location.href = '/maintenance?from=tickets';
              }
              // If maintenance mode is false, user can continue working
            } catch (error) {
              console.error('Failed to check maintenance status:', error);
              // On error, don't redirect to be safe
            }
          }
        });

        // Load cart from V2 API instead of window.viploge.cart
        await this.loadCartFromV2();
      };

      // Check if Ably is already connected (race condition fix)
      if (this.ably.connection.state === 'connected') {
        debugLog('Ably already connected, setting up channel immediately');
        await setupAblyChannel();
      }

      // Always register the event handler for future reconnections
      // But make sure we don't call setupAblyChannel twice if already connected
      this.ably.connection.on('connected', () => {
        // Only setup if not already done (check if channel exists)
        if (!this.channel) {
          debugLog('Ably reconnected, setting up channel...');
          setupAblyChannel();
        } else {
          debugLog('Ably reconnected, channel already set up');
        }
      });
    },
    unmounted() {
      this.stopProjectAvailabilityRefresh();
      this.stopCartReservationRefresh();

      window.removeEventListener('focus', this.handleProjectAvailabilityFocus);
      document.removeEventListener(
        'visibilitychange',
        this.handleProjectAvailabilityVisibilityChange
      );

      if (this.channel && this.channel.unsubscribe) {
        this.channel.unsubscribe();
      }
    },
    methods: {
      ...mapActions(userStore, ['hasRight']),
      ...mapActions(platformStore, [
        'setPlatform',
        'hasFeature',
        'setInitialFeatures',
      ]),
      ...mapActions(eventStore, [
        'getPublicEvents',
        'setTicketCounters',
        'updateTicketCounters',
      ]),
      ...mapActions(cartStore, [
        'setCart',
        'addToCart',
        'removeEventFromCart',
        'saveCart',
      ]),
      // Project navigation methods
      async loadProjects() {
        this.loadingProjects = true;
        try {
          // Ticketgroepen en projecten staan naast elkaar in het overzicht: een
          // activiteit met een groep hoort onder die groep, de rest onder haar project.
          // Valt het groepsendpoint weg, dan blijft de verkoop via de projecten werken.
          const [projectsResponse, groupsResponse] = await Promise.all([
            ProjectService.getProjectsForTickets(),
            TicketGroupService.getGroupsForTickets().catch((error) => {
              console.error('Failed to load ticket groups:', error);
              return { data: [] };
            }),
          ]);
          this.projects = [
            ...(groupsResponse.data || []),
            ...(projectsResponse.data || []),
          ];
        } catch (error) {
          console.error('Failed to load projects:', error);
        } finally {
          this.loadingProjects = false;
        }
      },
      async handleSelectProject(project) {
        this.loadingProjects = true;
        try {
          // Fetch full block details with events
          const response = isTicketGroup(project)
            ? await TicketGroupService.getGroupDetailForTickets(project.id)
            : await ProjectService.getProjectDetailForTickets(project.id);
          this.selectedProject = response.data;
          this.currentView = 'project-events';
          await this.refreshSelectedProjectAvailability(true);
          this.startProjectAvailabilityRefresh();

          // Update URL with the block ID
          const url = new URL(window.location);
          url.searchParams.delete('project');
          url.searchParams.delete('group');
          url.searchParams.set(
            isTicketGroup(project) ? 'group' : 'project',
            project.id
          );
          window.history.pushState({}, '', url);
        } catch (error) {
          console.error('Failed to load project details:', error);
        } finally {
          this.loadingProjects = false;
        }
      },
      handleBackToProjects() {
        this.stopProjectAvailabilityRefresh();
        this.projectAvailabilityLastLoadedAt = 0;
        this.selectedProject = null;
        this.currentView = 'projects';

        // Remove the block ID from URL
        const url = new URL(window.location);
        url.searchParams.delete('project');
        url.searchParams.delete('group');
        window.history.pushState({}, '', url);
      },
      async loadProjectFromUrl() {
        // Check if there's a block ID in the URL
        const url = new URL(window.location);
        const groupId = url.searchParams.get('group');
        const projectId = url.searchParams.get('project');

        if (this.projects.length === 0) {
          return;
        }

        const wantedType = groupId ? 'group' : 'project';
        const wantedId = parseInt(groupId || projectId);

        if (!wantedId) {
          return;
        }

        const block = this.projects.find(
          (p) => p.id === wantedId && p.type === wantedType
        );

        if (block) {
          // Fetch full block details with events
          await this.handleSelectProject(block);
        }
      },
      startProjectAvailabilityRefresh() {
        this.stopProjectAvailabilityRefresh();

        this.projectAvailabilityRefreshTimer = window.setInterval(() => {
          this.refreshSelectedProjectAvailability();
        }, PROJECT_AVAILABILITY_REFRESH_INTERVAL_MS);
      },
      stopProjectAvailabilityRefresh() {
        if (this.projectAvailabilityRefreshTimer) {
          window.clearInterval(this.projectAvailabilityRefreshTimer);
          this.projectAvailabilityRefreshTimer = null;
        }
      },
      stopCartReservationRefresh() {
        if (this.cartReservationRefreshTimer) {
          window.clearTimeout(this.cartReservationRefreshTimer);
          this.cartReservationRefreshTimer = null;
        }
      },
      scheduleCartReservationRefresh(cartData) {
        this.stopCartReservationRefresh();

        const items = Array.isArray(cartData?.items) ? cartData.items : [];
        const now = Date.now();
        let nextReservedUntil = null;

        for (const item of items) {
          const reservedUntil = Date.parse(item?.reserved_until || '');
          if (!Number.isFinite(reservedUntil) || reservedUntil <= now) {
            continue;
          }

          if (nextReservedUntil === null || reservedUntil < nextReservedUntil) {
            nextReservedUntil = reservedUntil;
          }
        }

        if (nextReservedUntil === null) {
          return;
        }

        const delay = Math.max(1000, nextReservedUntil - now + 1000);
        this.cartReservationRefreshTimer = window.setTimeout(async () => {
          this.cartReservationRefreshTimer = null;
          await this.loadCartFromV2(true);
        }, delay);
      },
      async handleProjectAvailabilityFocus() {
        await this.loadCartV2();
        this.refreshSelectedProjectAvailability();
      },
      async handleProjectAvailabilityVisibilityChange() {
        if (document.visibilityState === 'visible') {
          await this.loadCartV2();
          this.refreshSelectedProjectAvailability();
        }
      },
      async refreshSelectedProjectAvailability(force = false) {
        const projectId = this.selectedProject?.id;
        const projectType = this.selectedProject?.type;
        if (!projectId || this.currentView !== 'project-events') {
          return;
        }

        if (!force && document.visibilityState === 'hidden') {
          return;
        }

        const now = Date.now();
        if (
          !force &&
          (this.projectAvailabilityRefreshInFlight ||
            now - this.projectAvailabilityLastLoadedAt <
              PROJECT_AVAILABILITY_STALE_AFTER_MS)
        ) {
          return;
        }

        this.projectAvailabilityRefreshInFlight = true;

        try {
          const response =
            projectType === 'group'
              ? await TicketGroupService.getGroupAvailabilityForTickets(
                  projectId
                )
              : await ProjectService.getProjectAvailabilityForTickets(
                  projectId
                );

          if (
            !this.selectedProject ||
            this.selectedProject.id !== projectId ||
            this.selectedProject.type !== projectType ||
            this.currentView !== 'project-events'
          ) {
            return;
          }

          for (const eventAvailability of response.data || []) {
            this.updateEventFromBackend(eventAvailability);
          }

          this.projectAvailabilityLastLoadedAt = Date.now();
        } catch (error) {
          console.error('Error refreshing project availability:', error);
        } finally {
          this.projectAvailabilityRefreshInFlight = false;
        }
      },
      handleSelectEventFromProject(event) {
        this.selectEvent(event);
      },
      async loadCartV2(force = false) {
        const v2Store = cartV2Store();

        if (!force && !v2Store.needsRefresh(CART_CACHE_MAX_AGE_MS)) {
          debugLog('TicketsApp: Using cached V2 cart');
          return true;
        }

        return await this.loadCartFromV2(force);
      },

      usesFreeSeatSelection,
      usesTicketSelectionFlow,
      usesDeferredSeatAssignment,

      async selectEvent(event) {
        this.selectedEvent = event;

        // Load cart to get existing tickets for this event
        await this.loadCartV2();

        // Wait for Ably only for seat-selection events that rely on realtime updates.
        if (this.usesFreeSeatSelection(event) && !this.connected) {
          debugLog('Waiting for Ably connection before opening modal...');
          await this.waitForAblyConnection();
        }

        if (this.usesFreeSeatSelection(event)) {
          this.showSeatSelectionModal = true;
        } else {
          this.showTicketSelectionModal = true;
        }
      },
      waitForAblyConnection() {
        return new Promise((resolve) => {
          if (this.connected && this.channel) {
            resolve();
            return;
          }

          const checkConnection = () => {
            if (this.connected && this.channel) {
              resolve();
            } else {
              setTimeout(checkConnection, 100);
            }
          };

          checkConnection();
        });
      },
      selectEventToEdit(event) {
        const eventToEdit = this.events.find((e) => {
          return e.id === event.event.id;
        });
        this.selectEvent(eventToEdit);
      },
      backToOverview() {
        this.selectedEvent = null;
      },
      handleTicketSelectionCartUpdated(eventSummary) {
        if (!eventSummary) {
          return;
        }

        this.updateEventFromBackend({
          eventId: eventSummary.eventId ?? eventSummary.id,
          numTickets: eventSummary.numTickets ?? eventSummary.num_tickets,
          maxNumTickets:
            eventSummary.maxNumTickets ?? eventSummary.max_num_tickets,
          availableTickets:
            eventSummary.availableTickets ?? eventSummary.available_tickets,
        });
      },
      syncLegacyCartFromV2Data(data) {
        const oldFormatCart = {
          ticket: [],
        };

        for (const item of data.items || []) {
          if (item.type !== 'ticket') continue;

          const eventId = item.metadata?.event_id;
          if (!eventId) continue;

          let eventData = null;
          if (this.selectedProject?.events) {
            eventData = this.selectedProject.events.find(
              (e) => e.id === parseInt(eventId)
            );
          }

          let formattedDate = '';
          let formattedTime = '';

          if (eventData?.date) {
            formattedDate = dayjs(eventData.date).format('DD/MM/YYYY');
          }

          if (eventData?.startTime) {
            formattedTime = eventData.startTime;
          }

          for (let i = 0; i < item.quantity; i++) {
            oldFormatCart.ticket.push({
              event: {
                id: parseInt(eventId),
                title:
                  item.metadata?.event_title ||
                  item.metadata?.event_name ||
                  eventData?.name ||
                  '',
                description: eventData?.description || '',
                date: formattedDate,
                time: formattedTime,
                location: eventData?.location || '',
              },
              ticket: {
                index: item.metadata?.index || null,
                label: item.metadata?.seat_name || item.metadata?.seat || null,
                name: item.metadata?.name || '',
                code: item.metadata?.code || '',
                uitpasNummer: item.metadata?.uitpasNummer || '',
                type: {
                  id: item.item_id,
                  title: item.metadata?.ticket_name || '',
                  price: parseFloat(item.unit_price),
                },
                price: parseFloat(item.unit_price),
              },
            });
          }
        }

        this.setCart(oldFormatCart);
        this.numTickets = data.totals?.count || 0;
        this.amount = parseFloat(data.totals?.total || 0);
      },
      async addTicketsToCart(tickets) {
        // Support both ticketTypes and tickets properties
        const ticketTypes =
          this.selectedEvent.ticketTypes || this.selectedEvent.tickets || [];

        try {
          // Add each ticket via V2 API
          for (const ticket of tickets) {
            let type = ticketTypes.find((t) => {
              return t.id === ticket.type;
            });

            const metadata = {
              event_id: this.selectedEvent.id,
              event_name: this.selectedEvent.name || this.selectedEvent.title,
              ticket_type_name: type?.name || '',
              name: ticket.name || '',
              code: ticket.code || '',
              uitpasNummer: ticket.uitpasNummer || '',
            };

            // Add seat info if available
            if (ticket.label) {
              metadata.seat = ticket.label;
            }
            if (ticket.index) {
              metadata.index = ticket.index;
            }

            await fetch('/api/v2/cart/add-ticket', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                eventId: this.selectedEvent.id,
                ticketTypeId: ticket.type,
                quantity: 1,
                metadata: metadata,
              }),
            });
          }

          // Close modals
          this.showSeatSelectionModal = false;
          this.showTicketSelectionModal = false;

          // Note: No need to publish cart_seats anymore - backend publishes ticket.reserved events
          // which are received by all clients on the realtime channel

          // Reload cart from V2 API
          await this.loadCartFromV2(true);

          // Scroll to top to show cart after adding tickets
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        } catch (error) {
          console.error('Error adding tickets to cart:', error);
          alert(
            'Er is een fout opgetreden bij het toevoegen van tickets aan de winkelwagen.'
          );
        }
      },
      updateLocalTicketCount(eventId, ticketChange) {
        // Find the event and update its ticket count optimistically
        const event = this.events.find((e) => e.id === eventId);
        if (event) {
          event.num_tickets = (event.num_tickets || 0) + ticketChange;
          if (event.max_num_tickets) {
            event.available_tickets = Math.max(
              0,
              event.max_num_tickets - event.num_tickets
            );
          }
        }
      },
      closeCheckout() {
        this.showCheckout = false;
      },
      async openCheckout() {
        const cartLoaded = await this.loadCartFromV2(true);

        if (!cartLoaded) {
          return;
        }

        const v2Store = cartV2Store();
        if (!v2Store.hasItems) {
          debugLog('TicketsApp: Not opening checkout because cart is empty');
          return;
        }

        this.showCheckout = true;
      },
      async loadCartFromV2(force = false) {
        const v2Store = cartV2Store();

        try {
          const data = await v2Store.loadCart({
            force,
            maxAgeMs: CART_CACHE_MAX_AGE_MS,
          });

          debugLog('Cart loaded from V2 API:', data);

          if (!data?.items) {
            console.error('Invalid cart response - no items array');
            return false;
          }

          this.syncLegacyCartFromV2Data(data);
          this.scheduleCartReservationRefresh(data);

          if (
            this.currentView === 'project-events' &&
            this.selectedProject?.id
          ) {
            await this.refreshSelectedProjectAvailability(true);
          }

          return true;
        } catch (error) {
          console.error('Error loading cart from V2 API:', error);
          v2Store.setError(error.message);
          return false;
        }
      },
      closeCheckoutAndClear() {
        const v2Store = cartV2Store();

        this.setCart({});
        v2Store.clearCart();
        this.stopCartReservationRefresh();
        this.numTickets = 0;
        this.amount = 0;
        this.showCheckout = false;
        // Refresh project data instead of old getPublicEvents
        if (this.selectedProject) {
          this.loadProjects();
          this.refreshSelectedProjectAvailability(true);
        }
      },

      /**
       * Update event counters based on Ably real-time events
       * This method updates the local state without refreshing the page
       */
      /**
       * Update event data from backend Ably event
       * Backend is authoritative - it sends the calculated availableTickets
       * This method updates the local state without refreshing the page
       */
      updateEventFromBackend(data) {
        const { eventId, numTickets, maxNumTickets, availableTickets } = data;

        debugLog('Updating event from backend:', {
          eventId,
          numTickets,
          maxNumTickets,
          availableTickets,
        });

        const applyAvailabilityUpdate = (event) => {
          if (!event) {
            return false;
          }

          event.numTickets = numTickets;
          event.maxNumTickets = maxNumTickets;
          event.availableTickets = availableTickets;
          event.num_tickets = numTickets;
          event.max_num_tickets = maxNumTickets;
          event.available_tickets = availableTickets;

          return true;
        };

        let updated = false;

        if (this.selectedProject?.events) {
          const projectEvent = this.selectedProject.events.find(
            (e) => e.id === eventId
          );
          updated = applyAvailabilityUpdate(projectEvent) || updated;
        }

        if (this.events) {
          const listEvent = this.events.find((e) => e.id === eventId);
          updated = applyAvailabilityUpdate(listEvent) || updated;
        }

        if (this.selectedEvent?.id === eventId) {
          updated = applyAvailabilityUpdate(this.selectedEvent) || updated;
        }

        if (!updated) {
          debugLog('Event not found for backend update:', eventId);
          return;
        }

        debugLog('Event updated from backend:', {
          eventId,
          numTickets,
          maxNumTickets,
          availableTickets,
        });
      },
    },
  };
</script>

<template>
  <div class="mx-auto max-w-3xl sm:px-6 lg:max-w-7xl lg:px-8">
    <h1 class="sr-only">Tickets</h1>
    <!-- Main 3 column grid -->
    <div class="grid grid-cols-1 items-start gap-4 md:grid-cols-3 md:gap-8">
      <!-- Left column - order-2 on mobile, order-1 on tablet+ -->
      <div class="order-2 grid grid-cols-1 gap-4 md:order-1 md:col-span-2">
        <!-- Project Navigation (new) -->
        <template v-if="useProjectNavigation">
          <Spinner v-if="loadingProjects" />
          <ProjectsList
            v-else-if="currentView === 'projects'"
            :projects
            @select-project="handleSelectProject"
          />
          <ProjectEventsView
            v-else-if="currentView === 'project-events'"
            :project="selectedProject"
            :loading="loadingProjects"
            @back="handleBackToProjects"
            @select-event="handleSelectEventFromProject"
          />
        </template>

        <!-- @deprecated Old Event List (emergency fallback only) -->
        <!-- To rollback: set useProjectNavigation() to return false -->
        <!-- Can be removed after successful testing of project-based navigation -->
        <template v-else>
          <Spinner v-if="loadingEvents" />
          <Events v-else :events @select-event="selectEvent" />
        </template>
      </div>

      <!-- Shopping Cart -->
      <!-- Mobile: order-1 (top) if has items, hidden if empty -->
      <!-- Tablet+: order-2 (right), always visible -->
      <div
        class="grid-cols-1 gap-4 md:order-2"
        :class="{
          'order-1': hasCartItems,
          'hidden md:!grid': !hasCartItems,
        }"
      >
        <section aria-labelledby="cart-section">
          <ShoppingCart
            @checkout="openCheckout"
            @event-updated="handleTicketSelectionCartUpdated"
          />
        </section>
      </div>
    </div>
  </div>
  <CheckoutModal
    v-if="showCheckout"
    :open="showCheckout"
    @close="closeCheckout"
    @close-and-clear="closeCheckoutAndClear"
  />

  <!-- New Seat Selection Modal -->
  <SeatSelectionModal
    v-if="selectedEvent && usesFreeSeatSelection(selectedEvent)"
    :open="showSeatSelectionModal"
    :event="selectedEvent"
    :channel
    :sessionUserId
    :existingCartTickets="existingCartTicketsForSelectedEvent"
    @close="showSeatSelectionModal = false"
    @add-tickets-to-cart="addTicketsToCart"
    @back-to-overview="backToOverview"
  />

  <!-- Ticket Selection Modal (non-seat events + snake auto assignment events) -->
  <TicketSelectionModal
    v-if="selectedEvent && usesTicketSelectionFlow(selectedEvent)"
    :open="showTicketSelectionModal"
    :eventId="selectedEvent.id"
    :eventSummary="selectedEvent"
    :channel
    :sessionUserId
    :existingCartTickets="existingCartTicketsForSelectedEvent"
    :hasRight
    :deferredSeatAssignment="usesDeferredSeatAssignment(selectedEvent)"
    @cart-updated="handleTicketSelectionCartUpdated"
    @close="showTicketSelectionModal = false"
    @add-tickets-to-cart="addTicketsToCart"
    @back-to-overview="backToOverview"
  />
</template>
